import { useHeartBeatStatusChecker } from '@Hooks';
import { TFunc, useGlobalization } from '@Translations';
import {
	ComponentData,
	HeartBeatStatusIndicators,
	ReportedShadowStatus,
} from '@Types';
import formatDateTime from '@Utils/formatDateTime';
import {
	DataIcon,
	EmptyTableBody,
	StylovyzeTableBody,
	StylovyzeTableHeaderProps,
	useSettings,
} from '@innovyze/stylovyze';
import React, { ReactNode, useMemo } from 'react';
import { getComponentStatus } from '../utils/getComponentStatus';

const statusColors = new Map([
	[HeartBeatStatusIndicators.Green, '#4a7e04'],
	[HeartBeatStatusIndicators.Yellow, '#aa5d00'],
	[HeartBeatStatusIndicators.Red, '#aa0000'],
]);

const headers = (t: TFunc): StylovyzeTableHeaderProps[] => [
	{
		key: 'componentName',
		cell: t('Component'),
		width: '23%',
		align: 'left',
		sortable: false,
	},
	{
		key: 'status',
		cell: t('Status'),
		width: '23%',
		sortable: false,
	},
	{
		key: 'timestamp',
		cell: t('Timestamp'),
		width: '23%',
		sortable: false,
	},
	{
		key: 'version',
		cell: t('Version'),
		width: '23%',
		sortable: false,
	},
];

const ComponentStatusTable = () => {
	const { t } = useGlobalization();
	const { companySettings } = useSettings();
	const { dateFormat, hourCycle12, timeZoneIANA } = companySettings;
	const format = `${dateFormat} - ${hourCycle12 ? 'hh' : 'HH'}:mm ${
		hourCycle12 ? 'A' : ''
	}`;
	const { heartBeatStatus } = useHeartBeatStatusChecker();
	const reportedShadowStatus = heartBeatStatus?.status?.payload?.state
		?.reported as ReportedShadowStatus;

	const rows = useMemo(() => {
		if (reportedShadowStatus.state.length > 0) {
			const sortedComponents = [...reportedShadowStatus.state];
			sortedComponents.sort((a, b) => {
				return a.componentName.localeCompare(b.componentName);
			});

			return sortedComponents.map((component: ComponentData, index) => {
				const status = component?.state?.toLocaleLowerCase();
				let dateFormatted = '';

				if (component.timestamp)
					dateFormatted = formatDateTime(
						new Date(component.timestamp * 1000),
						timeZoneIANA,
						format,
					);

				const componentHealth: HeartBeatStatusIndicators =
					getComponentStatus(
						component.timestamp,
						component.state,
						component.componentName,
					);
				const color: string =
					statusColors.get(componentHealth) ||
					HeartBeatStatusIndicators.Red;

				const rowCells: ReactNode[] = [
					component.componentName,
					<div
						key={component.componentName}
						style={{ color, textTransform: 'capitalize' }}>
						{status}
					</div>,
					dateFormatted,
					component.version,
				];

				return {
					key: index,
					cells: rowCells,
				};
			});
		}
		return [];
	}, [reportedShadowStatus]);

	return (
		<StylovyzeTableBody
			dataCy="component status grid"
			headers={headers(t)}
			rows={rows}
			initialized={true}
			emptyTableBody={
				<div style={{ height: '50%' }}>
					<EmptyTableBody
						title={t(
							'There is no Health Status Information to display.',
						)}
						subtitle={t(
							'Reported Component Health Status information will be displayed here',
						)}
						icon={<DataIcon />}
						color={'#4A6067'}
						CTA={<></>}
					/>
				</div>
			}
		/>
	);
};

export default ComponentStatusTable;
